<script setup lang="ts">
import { ref, nextTick } from 'vue'
import { ChevronDown, ChevronUp, Filter } from 'lucide-vue-next'
import BBtn from '@lib/components/base/BBtn.vue'
import { useI18n } from 'vue-i18n'
import { useSearchShortcutTarget } from '@lib/composables/useSearchShortcut'

const props = defineProps<{
    activeCount?: number
}>()

const emit = defineEmits<{
    (e: 'apply'): void
    (e: 'clear'): void
}>()

const open = ref(false)
const panelRef = ref<HTMLElement | null>(null)
const { t } = useI18n()

function onApply() {
    emit('apply')
    open.value = false
}

// "/" anywhere on the page opens the filter and focuses the first text input
// (typically the search field). The shortcut is installed at App.vue and
// suppressed when the user is already typing in a field.
useSearchShortcutTarget(async () => {
    open.value = true
    await nextTick()
    const first = panelRef.value?.querySelector<HTMLInputElement>(
        'input[type="text"], input[type="search"], input:not([type])'
    )
    first?.focus()
    first?.select()
})
</script>

<template>
    <div class="bg-white border border-gray-200 rounded-12 mb-4 overflow-hidden">
        <button
            type="button"
            class="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors duration-fast"
            @click="open = !open"
        >
            <Filter class="w-4 h-4 text-gray-500" />
            <span class="font-medium text-gray-700">{{ t('common.actions.search') }}</span>
            <span v-if="props.activeCount" class="text-xs px-1.5 py-0.5 rounded-full bg-teal-l text-teal-d">{{
                props.activeCount
            }}</span>
            <component :is="open ? ChevronUp : ChevronDown" class="ml-auto w-4 h-4 text-gray-400" />
        </button>
        <div v-if="open" ref="panelRef" class="px-4 pb-4 border-t border-gray-200">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
                <slot />
            </div>
            <div class="flex justify-end gap-2 mt-3">
                <BBtn severity="secondary" size="small" :label="t('common.actions.clear')" @click="$emit('clear')" />
                <BBtn size="small" :label="t('common.actions.apply')" @click="onApply" />
            </div>
        </div>
    </div>
</template>
